import { CSSProperties, Fragment, PropsWithChildren, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import FocusLock from 'react-focus-lock';

import randomString from '@/utils/random-string';
import Portal from '@ui/portal/portal';
import useClickAwayListener from '@/utils/use-click-away-listener';

import styles from './bottom-sheet.module.css';
import { useCallbackRef } from '@/utils/use-callback-ref';

const ORIGINAL_MARGIN_TOP = document.body.style.marginTop || '0';
const ORIGINAL_MARGIN_LEFT = document.body.style.marginLeft || '0';
const ORIGINAL_MARGIN_RIGHT = document.body.style.marginRight || '0';
const ORIGINAL_MARGIN_BOTTOM = document.body.style.marginBottom || '0';

const ANIMATION_DRATION = 300;

type TBottomSheetProps = PropsWithChildren<{
  id?: string;
  portalClassName?: string;
  open?: boolean;
  height?: CSSProperties['height'];
  minHeight?: CSSProperties['minHeight'];
  maxHeight?: CSSProperties['maxHeight'];
  width?: CSSProperties['width'];
  unmountHiddenContent?: boolean;
  animationDirection?: 'up' | 'down' | 'left' | 'right';
  fade?: boolean;
  onClickAway?: (event: MouseEvent) => void;
  onEscape?: () => void;
  /** bodyMargin optionally adds additional spacing to the document body so everything on the page can still be scrolled to  */
  bodyMargin?: { top?: string; left?: string; right?: string; bottom?: string };
  trapFocus?: boolean;
  unmountContentIfHidden?: boolean;
  startUnmounted?: boolean;
  onOpened?: () => void;
  onClosed?: () => void;
  /** overrides the portal's stacking order -- needed when the sheet must render above another portal (e.g. a full-screen overlay) that also mounts to document.body */
  zIndex?: number;
}>;

const getPositionStyles = (direction = 'up') => {
  switch (direction) {
    case 'up':
      return {
        top: 'auto',
        left: '0',
        right: '0',
        bottom: '0',
      };
    case 'down':
      return {
        top: '0',
        left: '0',
        right: '0',
        bottom: 'auto',
      };
    case 'left':
      return {
        top: '0',
        left: '0',
        right: 'auto',
        bottom: '0',
      };
    case 'right':
      return {
        top: '0',
        left: 'auto',
        right: '0',
        bottom: '0',
      };
    default:
      return {
        top: '0',
        left: '0',
        right: '0',
        bottom: 'auto',
      };
  }
};

const getTransformState = (animationDirection: string, open?: boolean) => {
  if (open) {
    switch (animationDirection) {
      case 'up':
        return 'translateY(0)';
      case 'down':
        return 'translateY(0)';
      case 'left':
        return 'translateX(0)';
      case 'right':
        return 'translateX(0)';
      default:
        return 'translateY(0)';
    }
  } else {
    switch (animationDirection) {
      case 'up':
        return 'translateY(100%)';
      case 'down':
        return 'translateY(-100%)';
      case 'left':
        return 'translateX(-100%)';
      case 'right':
        return 'translateX(100%)';
      default:
        return 'translateY(100%)';
    }
  }
};

const BottomSheet = ({
  bodyMargin,
  open,
  portalClassName,
  id,
  children,
  height = '100%',
  minHeight = '0',
  maxHeight = 'none',
  width = '100%',
  animationDirection = 'up',
  fade,
  onClickAway,
  onEscape,
  trapFocus,
  unmountContentIfHidden = true,
  startUnmounted,
  onOpened,
  onClosed,
  zIndex,
}: TBottomSheetProps) => {
  const portalRef = useRef<HTMLDivElement>(null);
  const onOpenedRef = useCallbackRef(onOpened);
  const onClosedRef = useCallbackRef(onClosed);
  const marginTimer = useRef<NodeJS.Timeout | null>(null);
  const unmountTimer = useRef<NodeJS.Timeout | null>(null);

  const [shouldUnmountContent, setShouldUnmountContent] = useState(startUnmounted || unmountContentIfHidden);

  useEffect(() => {
    const body = document.body;
    body.classList.add(styles['margin-transitions']);

    body.style.marginTop = (open && bodyMargin?.top) || ORIGINAL_MARGIN_TOP;
    body.style.marginLeft = (open && bodyMargin?.left) || ORIGINAL_MARGIN_LEFT;
    body.style.marginRight = (open && bodyMargin?.right) || ORIGINAL_MARGIN_RIGHT;
    body.style.marginBottom = (open && bodyMargin?.bottom) || ORIGINAL_MARGIN_BOTTOM;
    if (marginTimer.current) {
      clearTimeout(marginTimer.current);
    }
    marginTimer.current = setTimeout(() => {
      body.classList.remove(styles['margin-transitions']);
      if (open) {
        onOpenedRef.current?.();
      } else {
        onClosedRef.current?.();
      }
    }, 300);

    return () => {
      if (marginTimer.current) {
        clearTimeout(marginTimer.current);
      }
      body.classList.remove(styles['margin-transitions']);
      body.style.marginTop = ORIGINAL_MARGIN_TOP;
      body.style.marginLeft = ORIGINAL_MARGIN_LEFT;
      body.style.marginRight = ORIGINAL_MARGIN_RIGHT;
      body.style.marginBottom = ORIGINAL_MARGIN_BOTTOM;
    };
  }, [bodyMargin, onClosedRef, onOpenedRef, open]);

  useClickAwayListener({
    ref: portalRef,
    open: !!open,
    onPointerDownOutside: onClickAway,
    onEscape: onEscape,
    ignoreClasses: ['.alter-theme', '.global-modal-body'],
  });

  const TrapWrapper = trapFocus ? FocusLock : Fragment;
  const trapProps = trapFocus ? { returnFocus: true, disabled: !open } : {};

  useEffect(() => {
    const portalEl = portalRef.current;
    if (!portalEl) return;

    if (startUnmounted && shouldUnmountContent && !unmountContentIfHidden && open) {
      setShouldUnmountContent(false);
    }

    if (!unmountContentIfHidden) return;

    if (open) {
      setShouldUnmountContent(false);
      return;
    }
    if (unmountTimer.current) {
      clearTimeout(unmountTimer.current);
    }
    unmountTimer.current = setTimeout(() => {
      setShouldUnmountContent(true);
    }, ANIMATION_DRATION);
    return () => {
      if (unmountTimer.current) {
        clearTimeout(unmountTimer.current);
      }
    };
  }, [open, shouldUnmountContent, startUnmounted, unmountContentIfHidden]);

  return (
    <Portal
      id={id || randomString(7)}
      className={classNames(styles['bottom-sheet-portal'], {
        [portalClassName || '']: !!portalClassName,
        [styles[animationDirection]]: animationDirection,
      })}
      aria-hidden={!open}
      ref={portalRef}
      style={
        {
          '--bottom-sheet-height': height,
          '--bottom-sheet-min-height': minHeight,
          '--bottom-sheet-max-height': maxHeight,
          '--bottom-sheet-width': width,
          ...getPositionStyles(animationDirection),
          '--bottom-sheet-animation-display': open ? 'block' : 'none',
          '--bottom-sheet-animation-opacity': !fade || open ? 1 : 0,
          '--bottom-sheet-animation-transform': getTransformState(animationDirection, open),
          '--bottom-sheet-starting-animation-transform': getTransformState(animationDirection, false),
          '--bottom-sheet-animation-duration': `${ANIMATION_DRATION}ms`,
          ...(zIndex !== undefined ? { zIndex } : {}),
        } as CSSProperties
      }
    >
      <TrapWrapper {...trapProps}>{shouldUnmountContent ? null : <>{children}</>}</TrapWrapper>
    </Portal>
  );
};

export default BottomSheet;
